import {
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useEffect, useRef } from "react";
import {
  FieldPath,
  FieldValues,
  PathValue,
  useController,
  useFormContext,
} from "react-hook-form";

import {
  BaseFieldProps,
  DependsOn,
  Options,
} from "../../../components/atoms/FieldProps";
import Text from "../../../components/atoms/Text";
import ArrowDownIcon from "../../../components/icons/arrow/ArrowDownIcon";
import useIsFieldDisabled from "../../../hooks/formHooks/useIsFieldDisabled";
import { mono } from "../../../theme/colors";

type Props<TFieldValues extends FieldValues> = {
  options: Options;
  dependsOn?: DependsOn<TFieldValues>;
  changeValue: (value: boolean) => void;
} & BaseFieldProps<TFieldValues>;

const fullData = [
  "ALL",
  "TH",
  "JP",
  "ID",
  "MY",
  "KH",
  "PH",
  "HK",
  "VN",
  "MO",
  "SG",
  "Others",
];

const MultiSelect = <TFieldValues extends FieldValues>({
  name,
  label,
  disabled = false,
  disabledForEdit = false,
  options,
  dependsOn,
  changeValue,
}: Props<TFieldValues>) => {
  const { control, clearErrors, watch, setError } =
    useFormContext<TFieldValues>();
  const { field, fieldState } = useController({
    name,
    control,
  });

  // TODO: This logic is duplicated extract into a HOC
  const valueToWatch = dependsOn?.field && watch(dependsOn?.field);
  const shouldValidate = !!dependsOn;
  const shouldEnable = !shouldValidate || valueToWatch === dependsOn.value;
  const fieldIsDisabled = useIsFieldDisabled(disabled, disabledForEdit);
  const errorMessage = fieldState.error?.message;

  const selectedOptionsRef = useRef<string[]>(field.value ?? []);

  useEffect(() => {
    if (selectedOptionsRef.current.find(val => val === "ALL")) {
      if (field.value.find((val: string) => val === "ALL")) {
        const filterAll = field.value.filter((item: string) => item !== "ALL");
        selectedOptionsRef.current = filterAll;
        changeValue(false);
      } else {
        changeValue(true);
        selectedOptionsRef.current = [];
      }
    } else {
      changeValue(false);
      if (field.value.find((val: string) => val === "ALL")) {
        selectedOptionsRef.current = fullData;
      } else {
        if (field.value.length === fullData.length - 1) {
          selectedOptionsRef.current = fullData;
        } else {
          selectedOptionsRef.current = field.value;
        }
      }
    }
  }, []);

  useEffect(() => {
    if (!shouldEnable) {
      if (selectedOptionsRef.current.length) {
        selectedOptionsRef.current = [];
      }
      field.onChange([] as PathValue<TFieldValues, FieldPath<TFieldValues>>);
      clearErrors(name);
    }
    if (shouldEnable && shouldValidate) {
      if (
        dependsOn.required &&
        (!selectedOptionsRef.current ||
          selectedOptionsRef.current?.length === 0)
      ) {
        setError(name, {
          message: dependsOn.message,
          type: "required",
        });
      } else {
        clearErrors(name);
      }
    }
  }, [shouldEnable, shouldValidate, name, selectedOptionsRef.current]);

  const handleSelect = (value: string[]) => {
    if (value) {
      if (selectedOptionsRef.current.find(val => val === "ALL")) {
        if (value.find(val => val === "ALL")) {
          const filterAll = value.filter(item => item !== "ALL");
          selectedOptionsRef.current = filterAll;
          changeValue(false);
        } else {
          changeValue(true);
          selectedOptionsRef.current = [];
        }
      } else {
        changeValue(false);
        if (value.find(val => val === "ALL")) {
          selectedOptionsRef.current = fullData;
        } else {
          if (value.length === fullData.length - 1) {
            selectedOptionsRef.current = fullData;
          } else {
            selectedOptionsRef.current = value;
          }
        }
      }
    }
  };

  const handleChange = (
    event: SelectChangeEvent<typeof selectedOptionsRef.current>,
  ) => {
    const {
      target: { value },
    } = event;
    const data = typeof value === "string" ? value.split(",") : value;
    handleSelect(data);
    field.onChange(value as PathValue<TFieldValues, FieldPath<TFieldValues>>);
    clearErrors(name);
  };

  return (
    <FormControl
      disabled={!shouldEnable || fieldIsDisabled}
      error={!!errorMessage}
      sx={{
        minWidth: 150,
        backgroundColor: mono.white,
      }}
    >
      <InputLabel id={`${label}-multi-select-checkbox-label`}>
        <Text variant={"body_small"}>{label}</Text>
      </InputLabel>
      <Select
        data-testid={`form-${label}-SELECT_MULTI`}
        labelId={`${label}-multi-select-checkbox-label`}
        id={`${label}-multi-select-checkbox`}
        multiple
        value={selectedOptionsRef.current}
        onChange={handleChange}
        renderValue={selected => {
          const valueMappedToLabel = selected.map(
            selected =>
              options.find(option => option.value === selected)?.label,
          );
          return (
            <Text variant={"body_small"} textTransform={"capitalize"}>
              {valueMappedToLabel.join(", ")}
            </Text>
          );
        }}
        IconComponent={props => (
          <Box {...props}>
            <ArrowDownIcon />
          </Box>
        )}
      >
        {options.map(option => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox
              checked={selectedOptionsRef.current.indexOf(option.value) > -1}
            />
            <Text variant={"button"} textTransform={"capitalize"}>
              {option.label}
            </Text>
          </MenuItem>
        ))}
      </Select>
      {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
    </FormControl>
  );
};

export default MultiSelect;
